import Box from '@mui/material/Box'
import type { ReactElement } from 'react'

import { KestrixButton } from '../buttons/KestrixButton/KestrixButton.tsx'
import { KestrixCloseIcon } from '../KestrixIcon.jsx'
import KestrixV2Typography from '../typography/KestrixTypography.tsx'
import type { KestrixNoticeBannerProps } from './KestrixNoticeBanner.types.ts'
import { noticeBannerTokens as tokens } from './notice-banner-tokens.ts'
import { NoticeBannerIcon } from './NoticeBannerIcon.tsx'

const closeButtonSx = (iconColor: string) =>
  ({
    flexShrink: 0,
    minWidth: 16,
    width: 16,
    height: 16,
    minHeight: 16,
    padding: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    color: iconColor,
    '&:hover': {
      background: 'transparent',
      border: 'none',
      color: iconColor,
      boxShadow: 'none'
    },
    '&:active': {
      background: 'transparent',
      border: 'none',
      color: iconColor,
      boxShadow: 'none'
    }
  }) as const

const recoveryActionSx = {
  flexShrink: 0,
  fontSize: tokens.sizing.fontSize,
  lineHeight: tokens.sizing.lineHeight,
  paddingLeft: '12px',
  paddingRight: '12px',
  border: 'none',
  '&:hover': {
    border: 'none'
  },
  '&:active': {
    border: 'none'
  }
} as const

/**
 * Inline notice: icon, message, optional text action.
 * Variants: info, warning, success, error (KDS Filter Notice Banner).
 */
export const KestrixNoticeBanner = ({
  message,
  variant = 'info',
  actionLabel,
  onAction,
  onDismiss,
  'data-testid': dataTestId = 'kestrix-notice-banner'
}: KestrixNoticeBannerProps): ReactElement => {
  const showAction = Boolean(actionLabel && onAction)
  const showDismiss = onDismiss != null
  const palette = tokens.variant[variant]
  const role = variant === 'error' ? 'alert' : 'status'

  return (
    <Box
      role={role}
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: tokens.sizing.height,
        px: `${tokens.sizing.paddingX}px`,
        py: `${tokens.sizing.paddingY}px`,
        gap: `${tokens.sizing.gap}px`,
        borderRadius: tokens.sizing.radius,
        border: `1px solid ${palette.border}`,
        backgroundColor: palette.surface,
        fontFamily: tokens.sizing.fontFamily
      }}
    >
      <NoticeBannerIcon variant={variant} color={palette.icon} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: '1 1 auto',
          flexWrap: 'wrap',
          minWidth: 0,
          columnGap: `${tokens.sizing.textGap}px`,
          rowGap: `${tokens.sizing.textGap}px`
        }}
      >
        <KestrixV2Typography
          variant="bodySmall"
          weight="medium"
          color="subtext-neutral"
          htmlTag="p"
          data-testid="kestrix-notice-banner-message"
          sx={{
            width: 'auto',
            flex: '1 1 auto',
            minWidth: 0,
            margin: 0,
            color: palette.message,
            whiteSpace: 'normal'
          }}
        >
          {message}
        </KestrixV2Typography>
        {showAction || showDismiss ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: `${tokens.sizing.textGap}px`,
              marginLeft: 'auto',
              flexShrink: 0
            }}
          >
            {showAction ? (
              <KestrixButton
                variant="subtle"
                size="small"
                icon={null}
                iconPosition="start"
                iconOnly={false}
                loading={false}
                onClick={onAction}
                data-testid="kestrix-notice-banner-action"
                sx={recoveryActionSx}
              >
                {actionLabel}
              </KestrixButton>
            ) : null}
            {showDismiss ? (
              <KestrixButton
                variant="subtle"
                size="small"
                icon={<KestrixCloseIcon size={12} color="currentColor" />}
                iconPosition="start"
                iconOnly
                loading={false}
                onClick={onDismiss}
                aria-label="Close"
                data-testid="kestrix-notice-banner-dismiss"
                sx={closeButtonSx(palette.message)}
              >
                Close
              </KestrixButton>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
